import { useMemo, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus } from "lucide-react";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardBody, CardExtra, CardHead } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import { SearchInput, Select } from "@/components/ui/Form.jsx";
import { Avatar, Callout, EmptyState, ProgressBar, Segmented } from "@/components/ui/Display.jsx";
import { TableWrap, Td, Th, Tr } from "@/components/ui/Table.jsx";
import Tag, { RoleTag } from "@/components/ui/Tag.jsx";
import Pagination, { PaginationInfo } from "@/components/ui/Pagination.jsx";
import { BarChart } from "@/components/charts/index.jsx";
import { useConfirm, useToast } from "@/components/ui/feedback.js";
import OrgFormModal from "@/components/super-admin/OrgFormModal.jsx";
import OrgEditModal from "@/components/super-admin/OrgEditModal.jsx";

import { superAdminService } from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { formatDate, formatLastActive, formatNumber } from "@/lib/format.js";
import { toneOf } from "@/lib/tokens.js";
import { cx } from "@/lib/cx.js";
import {
    ORG_STATUS_FILTERS,
    ROLE_FILTERS,
    TABS,
    activitySeries,
    filterOrganizations,
    growthSeries,
    organizationRows,
    packageSummary,
    platformStats,
    priceLabel,
    userRows,
} from "@/lib/superAdmin.js";

/*
 * Replaces frontend/app/(dashboard)/super-admin/page.js.
 *
 * Four tabs over the platform: organizations, cross-organization users,
 * analytics and packages. The route is already gated to superAdmin by a nested
 * RequireAuth — the old app only hid the sidebar entry, so typing the URL still
 * rendered the page.
 *
 * OVER-SEAT IS THE NUMBER THIS PAGE EXISTS FOR. Everything else here is
 * available elsewhere; an organization quietly past the seat cap it was sold is
 * only visible from the platform side, so it gets a stat card, a filter and a
 * bar on every row.
 */
const PAGE_SIZE = 15;
const NONE = [];

export default function SuperAdmin() {
    const queryClient = useQueryClient();
    const toast = useToast();
    const confirm = useConfirm();

    const [tab, setTab] = useState("organizations");
    const [orgSearch, setOrgSearch] = useState("");
    const [orgStatus, setOrgStatus] = useState("");
    const [userSearch, setUserSearch] = useState("");
    const [userRole, setUserRole] = useState("");
    const [page, setPage] = useState(1);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formError, setFormError] = useState("");

    const [overviewQuery, orgsQuery, usersQuery, packagesQuery] = useQueries({
        queries: [
            {
                queryKey: ["sa-overview"],
                queryFn: () => superAdminService.getOverview().then((r) => r.data?.data ?? {}),
            },
            {
                queryKey: ["sa-organizations"],
                // One page big enough for a platform's whole org list: the page
                // filters and sorts client-side, and a server page would make
                // "3 of 200" mean "3 of the 10 I happened to fetch".
                queryFn: () =>
                    superAdminService
                        .getOrganizations({ limit: 200, page: 1 })
                        .then((r) => r.data?.data ?? {}),
            },
            {
                queryKey: ["sa-users"],
                queryFn: () =>
                    superAdminService.getUsers({ limit: 500, page: 1 }).then((r) => r.data?.data ?? {}),
            },
            {
                queryKey: ["sa-packages"],
                queryFn: () =>
                    superAdminService.getAllPackages().then((r) => r.data?.data?.packages ?? []),
            },
        ],
    });

    const analyticsQuery = useQuery({
        queryKey: ["sa-analytics"],
        enabled: tab === "analytics",
        queryFn: () => superAdminService.getAnalytics().then((r) => r.data?.data ?? {}),
    });

    const orgs = useMemo(
        () => organizationRows(orgsQuery.data?.organizations ?? NONE),
        [orgsQuery.data],
    );
    const users = useMemo(() => userRows(usersQuery.data?.users ?? NONE), [usersQuery.data]);
    const packages = packagesQuery.data ?? NONE;
    const stats = useMemo(
        () => platformStats(orgs, overviewQuery.data?.overview),
        [orgs, overviewQuery.data],
    );

    const visibleOrgs = useMemo(
        () => filterOrganizations(orgs, { search: orgSearch, status: orgStatus }),
        [orgs, orgSearch, orgStatus],
    );

    const visibleUsers = useMemo(() => {
        const q = userSearch.trim().toLowerCase();
        return users.filter((u) => {
            if (userRole && u.role !== userRole) return false;
            if (q && !`${u.name} ${u.email} ${u.organizationName}`.toLowerCase().includes(q))
                return false;
            return true;
        });
    }, [users, userSearch, userRole]);

    const rows = tab === "users" ? visibleUsers : visibleOrgs;
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const current = Math.min(page, totalPages);
    const pageRows = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

    const switchTab = (next) => {
        setTab(next);
        setPage(1);
    };

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ["sa-organizations"] });
        queryClient.invalidateQueries({ queryKey: ["sa-overview"] });
    };

    /* ---- Mutations ---- */

    const createOrg = useMutation({
        mutationFn: (values) => superAdminService.createOrganization(values),
        onSuccess: (_res, values) => {
            toast(`${values.name} created`);
            setCreating(false);
            setFormError("");
            invalidate();
        },
        onError: (error) =>
            setFormError(apiErrorMessage(error, "Could not create this organization.")),
    });

    const updateOrg = useMutation({
        mutationFn: ({ id, body }) => superAdminService.updateOrganization(id, body),
        onSuccess: () => {
            toast("Organization updated");
            setEditing(null);
            setFormError("");
            invalidate();
        },
        onError: (error) => setFormError(apiErrorMessage(error, "Could not save this organization.")),
    });

    const toggleStatus = useMutation({
        mutationFn: ({ id, isActive }) =>
            superAdminService.toggleOrganizationStatus(id, { isActive }),
        onSuccess: (_res, { isActive, name }) => {
            toast(`${name} ${isActive ? "reactivated" : "suspended"}`, isActive ? "success" : "error");
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not change the status."), "error"),
    });

    const askSuspend = async (org) => {
        const suspending = org.isActive;
        const ok = await confirm({
            tone: suspending ? "danger" : "success",
            title: suspending ? `Suspend ${org.name}?` : `Reactivate ${org.name}?`,
            body: suspending
                ? `All ${org.totalUsers} accounts stop being able to sign in, and their desktop trackers sign out on the next settings sync — usually within a minute. Recorded data is kept.`
                : "Everyone in this organization can sign in and track again.",
            okLabel: suspending ? "Suspend organization" : "Reactivate",
        });
        if (ok) toggleStatus.mutate({ id: org.id, isActive: !org.isActive, name: org.name });
    };

    const loading = orgsQuery.isLoading || overviewQuery.isLoading;
    const error = orgsQuery.error ?? overviewQuery.error ?? usersQuery.error;

    return (
        <>
            <PageChrome breadcrumb={["Super Admin"]} title="Super Admin" />

            <main className="mx-auto flex w-full max-w-[1680px] flex-col gap-5 p-4 lg:p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h1 className="text-[22px] font-semibold tracking-tight">Super Admin</h1>
                        <p className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">
                            Every organization on this platform, and what each of them is entitled
                            to.
                        </p>
                    </div>
                    <Button variant="primary" onClick={() => setCreating(true)} data-create-org>
                        <Plus className="size-4" strokeWidth={2.2} />
                        New organization
                    </Button>
                </div>

                {error && (
                    <Callout tone="danger" data-error>
                        {apiErrorMessage(error, "Could not load the platform overview.")}
                    </Callout>
                )}

                {/* ---- Platform stats ---- */}
                <section className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
                    <Stat
                        label="Organizations"
                        value={formatNumber(stats.organizations)}
                        hint={`${stats.activeOrganizations} active · ${stats.suspended} suspended`}
                        tone="brand"
                        testId="organizations"
                    />
                    <Stat
                        label="Users"
                        value={formatNumber(stats.users)}
                        hint={`${formatNumber(stats.activeUsers)} active accounts`}
                        tone="emerald"
                        testId="users"
                    />
                    <Stat
                        label="Time entries (7 days)"
                        value={
                            stats.recentTimeEntries == null
                                ? "—"
                                : formatNumber(stats.recentTimeEntries)
                        }
                        hint="Across every organization"
                        tone="slate"
                        testId="entries"
                    />
                    <Stat
                        label="Over seat limit"
                        value={formatNumber(stats.overSeat)}
                        hint={
                            stats.overSeat
                                ? "An organization exceeds the package it was sold"
                                : "Every organization is within its package"
                        }
                        tone={stats.overSeat ? "amber" : "slate"}
                        testId="overseat"
                    />
                </section>

                {/* ---- Tabs ---- */}
                <Card>
                    <CardHead className="flex-wrap gap-3">
                        <Segmented
                            value={tab}
                            onChange={switchTab}
                            options={TABS.map((t) => ({
                                ...t,
                                count:
                                    t.value === "organizations"
                                        ? orgs.length
                                        : t.value === "users"
                                          ? users.length
                                          : t.value === "packages"
                                            ? packages.length
                                            : undefined,
                            }))}
                        />

                        <CardExtra>
                            {tab === "organizations" && (
                                <>
                                    <SearchInput
                                        value={orgSearch}
                                        onChange={(e) => {
                                            setOrgSearch(e.target.value);
                                            setPage(1);
                                        }}
                                        placeholder="Search name or identifier…"
                                        aria-label="Search organizations"
                                        className="w-60"
                                        data-filter="org-search"
                                    />
                                    <Select
                                        size="sm"
                                        value={orgStatus}
                                        onChange={(e) => {
                                            setOrgStatus(e.target.value);
                                            setPage(1);
                                        }}
                                        aria-label="Filter organizations"
                                        className="w-auto"
                                        data-filter="org-status"
                                    >
                                        {ORG_STATUS_FILTERS.map((f) => (
                                            <option key={f.value} value={f.value}>
                                                {f.label}
                                            </option>
                                        ))}
                                    </Select>
                                </>
                            )}

                            {tab === "users" && (
                                <>
                                    <SearchInput
                                        value={userSearch}
                                        onChange={(e) => {
                                            setUserSearch(e.target.value);
                                            setPage(1);
                                        }}
                                        placeholder="Search name, email or organization…"
                                        aria-label="Search users"
                                        className="w-72"
                                        data-filter="user-search"
                                    />
                                    <Select
                                        size="sm"
                                        value={userRole}
                                        onChange={(e) => {
                                            setUserRole(e.target.value);
                                            setPage(1);
                                        }}
                                        aria-label="Filter by role"
                                        className="w-auto"
                                        data-filter="user-role"
                                    >
                                        {ROLE_FILTERS.map((f) => (
                                            <option key={f.value} value={f.value}>
                                                {f.label}
                                            </option>
                                        ))}
                                    </Select>
                                </>
                            )}
                        </CardExtra>
                    </CardHead>

                    {tab === "organizations" && (
                        <OrganizationsTable
                            rows={pageRows}
                            loading={loading}
                            onEdit={setEditing}
                            onToggle={askSuspend}
                        />
                    )}

                    {tab === "users" && <UsersTable rows={pageRows} loading={loading} />}

                    {tab === "analytics" && <Analytics query={analyticsQuery} />}

                    {tab === "packages" && <Packages packages={packages} orgs={orgs} />}

                    {(tab === "organizations" || tab === "users") && rows.length > 0 && (
                        <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 px-4.5 py-3 dark:border-slate-800">
                            <PaginationInfo
                                page={current}
                                pageSize={PAGE_SIZE}
                                total={rows.length}
                                sizes={[PAGE_SIZE]}
                                noun={tab === "users" ? "users" : "organizations"}
                            />
                            <div className="ml-auto">
                                <Pagination
                                    page={current}
                                    totalPages={totalPages}
                                    onChange={setPage}
                                />
                            </div>
                        </div>
                    )}
                </Card>
            </main>

            {creating && (
                <OrgFormModal
                    packages={packages}
                    saving={createOrg.isPending}
                    error={formError}
                    onSubmit={(values) =>
                        createOrg.mutate({
                            name: values.name,
                            identifier: values.identifier,
                            packageId: values.packageId,
                            adminName: values.adminName,
                            email: values.email,
                            password: values.password,
                        })
                    }
                    onClose={() => {
                        setCreating(false);
                        setFormError("");
                    }}
                />
            )}

            {editing && (
                <OrgEditModal
                    organization={editing}
                    packages={packages}
                    saving={updateOrg.isPending}
                    error={formError}
                    onSubmit={(body) => updateOrg.mutate({ id: editing.id, body })}
                    onClose={() => {
                        setEditing(null);
                        setFormError("");
                    }}
                />
            )}
        </>
    );
}

/* ---- Pieces -------------------------------------------------------------- */

function Stat({ label, value, hint, tone, testId }) {
    const t = toneOf(tone);
    return (
        <article
            data-stat={testId}
            className="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-slate-800 dark:bg-slate-900"
        >
            <span className={cx("absolute inset-y-0 left-0 w-0.5", t.edge)} aria-hidden="true" />
            <p className="text-[12.5px] font-medium text-slate-600 dark:text-slate-400">{label}</p>
            <p
                data-stat-value
                className={cx(
                    "mt-1.5 text-[22px] leading-none font-semibold tracking-tight",
                    tone === "slate" ? undefined : t.text,
                )}
            >
                {value}
            </p>
            <p className="mt-1.5 text-[11.5px] text-slate-400 dark:text-slate-500">{hint}</p>
        </article>
    );
}

function OrganizationsTable({ rows, loading, onEdit, onToggle }) {
    if (!rows.length) {
        return (
            <CardBody>
                <EmptyState
                    icon={<Building2 className="size-9 opacity-50" strokeWidth={1.5} />}
                    title={loading ? "Loading organizations…" : "No organizations match this filter"}
                />
            </CardBody>
        );
    }

    return (
        <TableWrap minWidth="1080px">
            <thead>
                <tr>
                    <Th className="pl-4.5">Organization</Th>
                    <Th>Package</Th>
                    <Th className="w-48">Seats</Th>
                    <Th>Storage</Th>
                    <Th>Created</Th>
                    <Th>Last activity</Th>
                    <Th className="w-44 pr-4.5 text-right">Actions</Th>
                </tr>
            </thead>
            <tbody>
                {rows.map((o) => (
                    <Tr key={o.id} data-org={o.id}>
                        <Td className="pl-4.5">
                            <span className="flex min-w-0 items-center gap-2.5">
                                <Avatar name={o.name} size="md" />
                                <span className="min-w-0">
                                    <span className="flex flex-wrap items-center gap-2">
                                        <span className="truncate text-[13px] font-medium">
                                            {o.name}
                                        </span>
                                        {!o.isActive && <Tag tone="rose">Suspended</Tag>}
                                        {o.trackMode === "silent" && <Tag tone="amber">Silent</Tag>}
                                        {o.clipboardAllowed && <Tag tone="violet">Clipboard</Tag>}
                                    </span>
                                    <span className="block truncate font-mono text-[11.5px] text-slate-400 dark:text-slate-500">
                                        {o.identifier}
                                    </span>
                                </span>
                            </span>
                        </Td>

                        <Td className="text-slate-500 dark:text-slate-400">{o.packageName}</Td>

                        <Td>
                            {o.seatCap == null ? (
                                <span className="text-[12.5px] text-slate-500 dark:text-slate-400">
                                    {formatNumber(o.totalUsers)} · no cap
                                </span>
                            ) : (
                                <span className="block" data-seats={o.id}>
                                    <span
                                        className={cx(
                                            "mb-1 block font-mono text-[12px]",
                                            o.overSeat && "font-semibold text-amber-600 dark:text-amber-400",
                                        )}
                                    >
                                        {formatNumber(o.totalUsers)} / {formatNumber(o.seatCap)}
                                        {o.overSeat && " — over"}
                                    </span>
                                    <ProgressBar
                                        percent={o.seatPercent}
                                        tone={o.overSeat ? "amber" : "brand"}
                                    />
                                </span>
                            )}
                        </Td>

                        <Td>
                            <Tag tone={o.ownBucket ? "brand" : "slate"}>
                                {o.ownBucket ? "Own bucket" : "Platform"}
                            </Tag>
                        </Td>

                        <Td className="text-slate-500 dark:text-slate-400">
                            {o.createdAt ? formatDate(o.createdAt) : "—"}
                        </Td>

                        <Td className="text-slate-500 dark:text-slate-400">
                            {formatLastActive(o.lastActivity)}
                        </Td>

                        <Td className="pr-4.5">
                            <span className="flex items-center justify-end gap-2">
                                <Button onClick={() => onEdit(o)} data-edit={o.id}>
                                    Edit
                                </Button>
                                <Button
                                    variant={o.isActive ? "dangerSoft" : "default"}
                                    onClick={() => onToggle(o)}
                                    data-toggle-status={o.id}
                                >
                                    {o.isActive ? "Suspend" : "Reactivate"}
                                </Button>
                            </span>
                        </Td>
                    </Tr>
                ))}
            </tbody>
        </TableWrap>
    );
}

function UsersTable({ rows, loading }) {
    if (!rows.length) {
        return (
            <CardBody>
                <EmptyState title={loading ? "Loading users…" : "No users match this filter"} />
            </CardBody>
        );
    }

    return (
        <TableWrap minWidth="900px">
            <thead>
                <tr>
                    <Th className="pl-4.5">Name</Th>
                    <Th>Organization</Th>
                    <Th>Role</Th>
                    <Th>Status</Th>
                    <Th className="pr-4.5">Last active</Th>
                </tr>
            </thead>
            <tbody>
                {rows.map((u) => (
                    <Tr key={u.id} data-user={u.id}>
                        <Td className="pl-4.5">
                            <span className="flex min-w-0 items-center gap-2.5">
                                <Avatar name={u.name} size="md" />
                                <span className="min-w-0">
                                    <span className="block truncate text-[13px] font-medium">
                                        {u.name}
                                    </span>
                                    <span className="block truncate text-[11.5px] text-slate-400 dark:text-slate-500">
                                        {u.email}
                                    </span>
                                </span>
                            </span>
                        </Td>
                        <Td className="text-slate-500 dark:text-slate-400">{u.organizationName}</Td>
                        <Td>
                            <RoleTag role={u.role} />
                        </Td>
                        <Td>
                            <Tag tone={u.isActive ? "emerald" : "slate"}>
                                {u.isActive ? "Active" : "Deactivated"}
                            </Tag>
                        </Td>
                        <Td className="pr-4.5 text-slate-500 dark:text-slate-400">
                            {formatLastActive(u.lastActive)}
                        </Td>
                    </Tr>
                ))}
            </tbody>
        </TableWrap>
    );
}

function Analytics({ query }) {
    const orgGrowth = useMemo(
        () => growthSeries(query.data?.organizationGrowth ?? NONE),
        [query.data],
    );
    const userGrowth = useMemo(() => growthSeries(query.data?.userGrowth ?? NONE), [query.data]);
    const activity = useMemo(() => activitySeries(query.data?.activityByOrg ?? NONE), [query.data]);

    if (query.isLoading) {
        return (
            <CardBody>
                <p className="py-10 text-center text-[13px] text-slate-400 dark:text-slate-500">
                    Loading analytics…
                </p>
            </CardBody>
        );
    }

    if (query.isError) {
        return (
            <CardBody>
                <Callout tone="danger">
                    {apiErrorMessage(query.error, "Could not load analytics.")}
                </Callout>
            </CardBody>
        );
    }

    return (
        <CardBody className="space-y-6">
            <Chart title="Organizations created" data={orgGrowth} />
            <Chart title="Users created" data={userGrowth} />
            <Chart title="Time entries by organization" data={activity.slice(0, 12)} />
        </CardBody>
    );
}

function Chart({ title, data }) {
    return (
        <div>
            <p className="mb-2 text-[11px] font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                {title}
            </p>
            {data.length === 0 ? (
                <p className="py-6 text-center text-[13px] text-slate-400 dark:text-slate-500">
                    Nothing recorded in this window.
                </p>
            ) : (
                <BarChart data={data} height={200} format={(v) => formatNumber(Math.round(v))} />
            )}
        </div>
    );
}

function Packages({ packages, orgs }) {
    if (!packages.length) {
        return (
            <CardBody>
                <EmptyState
                    title="No packages defined"
                    hint="Run backend/scripts/create-default-packages.js to seed the standard plans."
                />
            </CardBody>
        );
    }

    return (
        <CardBody className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {packages.map((p) => {
                const s = packageSummary(p);
                // How many organizations are actually on this plan — the number
                // that says whether a plan is worth keeping.
                const inUse = orgs.filter((o) => o.packageId === p._id).length;

                return (
                    <article
                        key={p._id}
                        data-package={p._id}
                        className={cx(
                            "flex flex-col rounded-xl border p-4",
                            p.isPopular
                                ? "border-brand-300 ring-1 ring-brand-500/20 dark:border-brand-700"
                                : "border-slate-200 dark:border-slate-800",
                        )}
                    >
                        <div className="flex flex-wrap items-center gap-2">
                            <p className="text-[15px] font-semibold">{p.name}</p>
                            {p.isPopular && <Tag tone="brand">Popular</Tag>}
                            {p.isActive === false && <Tag tone="slate">Retired</Tag>}
                        </div>

                        <p className="mt-0.5 text-[12.5px] text-slate-500 dark:text-slate-400">
                            {p.description}
                        </p>

                        <p className="mt-3 text-[18px] font-semibold tracking-tight">
                            {priceLabel(p)}
                        </p>

                        <dl className="mt-3 space-y-1.5 text-[12.5px]">
                            <Row label="Seats" value={s.seats} />
                            <Row label="Projects" value={s.projects} />
                            <Row label="Teams" value={s.teams} />
                            <Row label="Screenshot storage" value={s.storage} />
                            <Row label="Retention" value={s.retention} />
                        </dl>

                        {s.flags.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {s.flags.map((f) => (
                                    <Tag key={f} tone="emerald">
                                        {f}
                                    </Tag>
                                ))}
                            </div>
                        )}

                        <p className="mt-auto pt-3 text-[11.5px] text-slate-400 dark:text-slate-500">
                            {inUse} organization{inUse === 1 ? "" : "s"} on this plan
                        </p>
                    </article>
                );
            })}
        </CardBody>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
            <dd className="font-medium">{value}</dd>
        </div>
    );
}
